//----------------------------------------------------
// FotosCard.jsx — Logboek24
//----------------------------------------------------
import React, { useEffect, useMemo, useState } from "react";
import Win10Card from "../Win10Card";
import {
  createObjectUrlFromDocument,
  isSecureFileUrl,
  resolveAssetUrl,
} from "../../utils/secureFileBlobs";

function resolvePhotoUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/api/files/") || /^https?:\/\/[^/]+\/api\/files\//i.test(raw)) {
    return resolveAssetUrl(raw);
  }
  if (raw.startsWith("/uploads/") || raw.includes("/")) return resolveAssetUrl(raw);
  return resolveAssetUrl(`/uploads/werkbonnen/${encodeURIComponent(raw)}`);
}

export default function FotosCard({ werkbon, onAddFoto }) {
  const [previewUrls, setPreviewUrls] = useState({});
  const fotos = useMemo(() => {
    const source = werkbon || {};
    const modern = Array.isArray(source?.photos) ? source.photos : [];
    const mappedModern = modern
      .map((photo, index) => ({
        id: String(photo?._id || `photo-${index}`),
        url: resolvePhotoUrl(photo?.url || photo?.filename || ""),
        thumbUrl: resolvePhotoUrl(photo?.thumbnailUrl || photo?.thumbnailFilename || ""),
      }))
      .filter((photo) => Boolean(photo.url));

    if (mappedModern.length > 0) return mappedModern;

    const legacy = Array.isArray(source?.fotos) ? source.fotos : [];
    return legacy
      .map((value, index) => ({
        id: `legacy-${index}`,
        url: resolvePhotoUrl(value),
        thumbUrl: resolvePhotoUrl(value),
      }))
      .filter((photo) => Boolean(photo.url));
  }, [werkbon]);

  useEffect(() => {
    const secureFotos = fotos.filter((photo) =>
      isSecureFileUrl(photo?.thumbUrl || photo?.url || "")
    );
    if (secureFotos.length === 0) {
      setPreviewUrls({});
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];

    (async () => {
      const entries = await Promise.all(
        secureFotos.map(async (photo) => {
          try {
            const objectUrl = await createObjectUrlFromDocument(photo.thumbUrl || photo.url);
            objectUrls.push(objectUrl);
            return [photo.id, objectUrl];
          } catch {
            return [photo.id, ""];
          }
        })
      );

      if (cancelled) {
        objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
        return;
      }

      setPreviewUrls(Object.fromEntries(entries.filter(([key, value]) => key && value)));
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [fotos]);

  async function openPhoto(url) {
    try {
      const objectUrl = await createObjectUrlFromDocument(url);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      window.alert("Foto openen mislukt.");
    }
  }

  if (!werkbon) return null;

  return (
    <Win10Card title="Foto's">
      {fotos.length > 0 ? (
        <div className="flex gap-3 overflow-x-auto">
          {fotos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => openPhoto(photo.url)}
              className="block"
            >
              {previewUrls[photo.id] || !isSecureFileUrl(photo.thumbUrl || photo.url) ? (
                <img
                  src={previewUrls[photo.id] || photo.thumbUrl || photo.url}
                  alt={`Werkbon foto ${index + 1}`}
                  onError={(event) => {
                    const target = event.currentTarget;
                    if (photo.url && target.src !== photo.url) {
                      target.src = photo.url;
                    }
                  }}
                  className="h-32 w-32 rounded border object-cover shadow-sm"
                />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded border bg-slate-100 text-xs text-slate-500">
                  Laden...
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="italic text-gray-500">Nog geen foto's toegevoegd.</p>
      )}

      <button
        onClick={onAddFoto}
        className="mt-3 rounded bg-blue-600 px-3 py-2 text-white transition hover:bg-blue-700"
      >
        Foto toevoegen
      </button>
    </Win10Card>
  );
}
